import React, { useRef, useState } from 'react';
import { Modal, Platform, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { useCoworkerStore, useToastStore } from '@/store';
import { WORK_APPROVAL_MODES, workApprovalMode } from '@/constants/workApproval';

export default function WorkApprovalButton({ taskId = null, compact = false, isDisabled = false, openAbove = false }) {
  const { colors } = useTheme();
  const { width, height } = useWindowDimensions();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [menuHeight, setMenuHeight] = useState(300);
  const triggerRef = useRef(null);
  const composerMode = useCoworkerStore(s => s.composerActionApproval);
  const taskMode = useCoworkerStore(s => taskId ? s.tasks[taskId]?.actionApproval : null);
  const taskStatus = useCoworkerStore(s => taskId ? s.tasks[taskId]?.status : null);
  const setComposerMode = useCoworkerStore(s => s.setComposerActionApproval);
  const setTaskMode = useCoworkerStore(s => s.setTaskActionApproval);
  const addToast = useToastStore(s => s.actions.addToast);
  const mode = workApprovalMode(taskId ? taskMode : composerMode);
  const disabled = isDisabled || saving || taskStatus === 'queued';
  const narrow = compact || width < 600;
  const color = mode.id === 'full' ? colors.color_warning || '#d97706' : colors.text_color_secondary;

  const choose = async next => {
    if (disabled) return;
    if (next === mode.id) { setOpen(false); return; }
    setSaving(true);
    try {
      if (taskId) await setTaskMode(taskId, next);
      else setComposerMode(next);
      setOpen(false);
    } catch (error) { addToast(error?.message || 'Could not change action approvals', 'error'); }
    finally { setSaving(false); }
  };
  const show = () => {
    setOpen(true);
    const node = triggerRef.current;
    if (node?.getBoundingClientRect) { setAnchor(node.getBoundingClientRect()); }
    else if (node?.measureInWindow) node.measureInWindow((x, y, w, h) => { setAnchor({ left: x, top: y, bottom: y + h }); });
  };
  const Trigger = Platform.OS === 'macos' ? DropdownMenu.Trigger : Pressable;
  const trigger = (
    <Tooltip label="Choose when Work asks before taking action" disabled={open || disabled}>
      <Trigger {...(Platform.OS === 'macos' ? {} : { onPress: show })} disabled={disabled}
        accessibilityRole="button" accessibilityLabel={`Action approvals: ${mode.label}`}
        accessibilityState={{ expanded: open, disabled, busy: saving }}
        {...(Platform.OS === 'web' ? { 'aria-expanded': open, 'aria-busy': saving } : {})}>
        <View style={[styles.trigger, { opacity: disabled ? 0.5 : 1 }]}>
          <Icon name={mode.icon} size={14} color={color} />
          {!narrow && <Text size="s" numberOfLines={1} style={{ color }}>{mode.label}</Text>}
          <Icon name="chevron-down" size={8} color={color} />
        </View>
      </Trigger>
    </Tooltip>
  );
  const panel = (
    <View style={styles.panel}>
      <Text size="s" weight="semibold" style={styles.heading}>When should Work check with you?</Text>
      {WORK_APPROVAL_MODES.map(option => {
        const selected = mode.id === option.id;
        const optionColor = option.id === 'full' ? colors.color_warning || '#d97706' : colors.text_color_primary;
        return <Pressable key={option.id} onPress={() => choose(option.id)} disabled={disabled}
          accessibilityRole="radio" accessibilityLabel={`${option.label}. ${option.description}`}
          accessibilityState={{ checked: selected, disabled }}
          {...(Platform.OS === 'web' ? { 'aria-checked': selected } : {})}
          style={({ hovered, pressed }) => [styles.option, (hovered || pressed || selected) && { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name={option.icon} size={18} color={optionColor} />
          <View style={styles.copy}>
            <Text size="s" weight="semibold" style={{ color: optionColor }}>{option.label}</Text>
            <Text size="xs" color="secondary">{option.description}</Text>
          </View>
          <View style={styles.check}>{selected && <Icon name="check" size={14} color={optionColor} />}</View>
        </Pressable>;
      })}
      <Text size="xs" color="tertiary" style={styles.footnote}>Applies to this task. Connected folders, app access, and credit settings still apply.</Text>
    </View>
  );
  if (Platform.OS === 'macos') return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      {trigger}
      <DropdownMenu.Content side={openAbove ? 'top' : 'bottom'} align="start" sideOffset={8}
        style={{ width: Math.min(420, width - 24), maxWidth: width - 24 }}>{panel}</DropdownMenu.Content>
    </DropdownMenu.Root>
  );
  const sheet = width < 600 || !anchor;
  const above = anchor && anchor.top >= menuHeight + 8 && (openAbove || height - anchor.bottom < menuHeight + 8);
  return (
    <View ref={triggerRef} {...(Platform.OS === 'web' ? {} : { collapsable: false })}>
      {trigger}
      <Modal visible={open} transparent animationType={sheet ? 'fade' : 'none'} onRequestClose={() => setOpen(false)}>
        <Pressable style={[styles.backdrop, sheet && styles.sheetBackdrop]} onPress={() => setOpen(false)}
          {...(Platform.OS === 'web' ? { onKeyDown: event => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); } } } : {})}>
          <Pressable onPress={() => {}} accessibilityViewIsModal onLayout={event => setMenuHeight(event.nativeEvent.layout.height)}
            style={[styles.card, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary,
              width: Math.min(420, width - 24), maxHeight: height - 32 }, !sheet && { position: 'absolute',
              left: Math.max(12, Math.min(anchor.left, width - 432)),
              top: Math.max(8, Math.min(above ? anchor.top - menuHeight - 6 : anchor.bottom + 6, height - menuHeight - 8)) }]}>
            <ScrollView keyboardShouldPersistTaps="handled">{panel}</ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  trigger: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 34, paddingHorizontal: 9, borderRadius: 18 },
  panel: { padding: 8, gap: 3 },
  heading: { paddingHorizontal: 10, paddingTop: 7, paddingBottom: 9 },
  option: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 10, paddingVertical: 12, borderRadius: 10 },
  copy: { flex: 1, gap: 4 },
  check: { width: 16 },
  footnote: { padding: 10, paddingBottom: 6 },
  backdrop: { flex: 1 },
  sheetBackdrop: { backgroundColor: '#0006', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 24 },
  card: { borderWidth: 1, borderRadius: 18, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 8px 32px #0003' } }) },
});
